import * as Types from '@frameio/graphql-client/generated/types';

import { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core';
import gql from 'graphql-tag';

import { useQuery, useLazyQuery, useMutation, useFragment, useSubscription } from '@apollo/client';
import type { QueryHookOptions, LazyQueryHookOptions, MutationHookOptions, SubscriptionHookOptions, UseFragmentOptions } from '@apollo/client';

export type PaymentMethodInfo_PaymentCard_Fragment = { __typename: 'PaymentCard', preferredLocale?: Types.SupportedLocale | null, taxId?: string | null, taxType?: string | null, card?: { __typename?: 'CardInfo', brand: string, expMonth: number, expYear: number, last4: string, name?: string | null } | null, billingAddress?: { __typename?: 'BillingAddress', city?: string | null, country?: string | null, line1?: string | null, line2?: string | null, postalCode?: string | null, state?: string | null } | null };

export type PaymentMethodInfo_PaymentCheck_Fragment = { __typename: 'PaymentCheck' };

export type PaymentMethodInfoFragment = PaymentMethodInfo_PaymentCard_Fragment | PaymentMethodInfo_PaymentCheck_Fragment;

export type SubscriptionBalanceInfoFragment = { __typename?: 'AccountSubscription', id: string, balance: number };

export type SubscriptionInfoFragment = { __typename?: 'AccountSubscription', cancelledAt?: string | null, isLegacy?: boolean | null, cost?: number | null, discountOfferExpiresAt?: string | null, id: string, nextBillAt?: string | null, pauseOfferExpiresAt?: string | null, period: Types.SubscriptionPeriod, subscriptionEndAt?: string | null, tier: Types.AccountTier, title: string, balance: number, addOns?: Array<{ __typename?: 'AddOn', cost: number, id: string, increment: number, modifier: string, quantity: number }> | null, limitedResources?: { __typename?: 'LimitedResources', archivedStorageBytes?: { __typename?: 'LimitedResource', limit53?: number | null, used53?: number | null } | null, mountedStorageBytes?: { __typename?: 'LimitedResource', limit53?: number | null, used53?: number | null } | null, projects?: { __typename?: 'LimitedResource', limit53?: number | null, used53?: number | null } | null, storageBytes?: { __typename?: 'LimitedResource', limit53?: number | null, used53?: number | null } | null, workspaces?: { __typename?: 'LimitedResource', limit53?: number | null, used53?: number | null } | null } | null, perUserStorage?: { __typename?: 'AccountSubscriptionPerUserStorage', seats: number, increment: number, limit: number } | null, plan?: { __typename?: 'Plan', activeStorageLimit?: number | null, archivedStorageLimit?: number | null, cost: number, id: string, tier: Types.PlanTier, title: string, eligibility: Types.PlanEligibility, mountedStorageLimit?: number | null, name?: string | null, userLimit?: number | null, userMax?: number | null, period: Types.PlanPeriod, perUserStorageIncrement?: number | null, version?: number | null, availableFeatures: { __typename?: 'AvailableFeatures', id: string, mountedStorage: boolean, mountedStorageOverprovisioningAllowed: boolean }, addOns?: Array<{ __typename?: 'PlanAddOn', id: string, modifier?: string | null }> | null } | null, trial: { __typename?: 'SubscriptionTrial', isActive: boolean, promotionExpiresAt?: string | null } };

export type PlanSuggestionInfoFragment = { __typename?: 'PlanSuggestion', feature: string, includedInCurrentPlan: boolean, plan?: { __typename?: 'Plan', cost: number, id: string, name?: string | null, period: Types.PlanPeriod, tier: Types.PlanTier, title: string, userLimit?: number | null, version?: number | null } | null };

export type AccountOwnerFragment = { __typename?: 'Account', id: string, owner?: { __typename?: 'Owner', id: string, name: string, email: string } | null };

export type AccountPlanAndTrialInfoFragment = { __typename?: 'Account', id: string, subscription: { __typename?: 'AccountSubscription', discountOfferExpiresAt?: string | null, id: string, plan?: { __typename?: 'Plan', cost: number, id: string, name?: string | null, paymentMethod: Types.PlanPaymentMethod, period: Types.PlanPeriod, tier: Types.PlanTier, version?: number | null } | null, trial: { __typename?: 'SubscriptionTrial', isActive: boolean } } };

export type AccountManagedByAdobeFragment = { __typename?: 'Account', managedBy?: Types.AccountManagementTypes | null };

export type AccountLockingInfoFragment = { __typename?: 'Account', id: string, lockedAt?: string | null, lockReason?: Types.LockReason | null };

export type AccountRoleInfoFragment = { __typename?: 'Account', role?: { __typename?: 'Role', id: string, name: string } | null };

export type AccountLevelMetadataEnabledFragment = { __typename?: 'Account', useAccountScopedFieldDefinitions: boolean };

export type AccountAdobeManagedDetailsFragment = { __typename?: 'Account', managedBy?: Types.AccountManagementTypes | null, adobeOrg?: string | null };

export type AccountTierFieldsFragment = { __typename?: 'Account', id: string, tier?: Types.AccountTier | null };

export type AccountTrialStatusFragment = { __typename?: 'Account', id: string, subscription: { __typename?: 'AccountSubscription', id: string, trial: { __typename?: 'SubscriptionTrial', isActive: boolean } } };

export type AccountPromotionalBannerOptOutFragment = { __typename?: 'Account', promotionalBannerOptOut?: boolean | null };

export const PaymentMethodInfo = /*#__PURE__*/ gql`
    fragment PaymentMethodInfo on PaymentMethod {
  ... on PaymentCard {
    __typename
    card {
      brand
      expMonth
      expYear
      last4
      name
    }
    billingAddress {
      city
      country
      line1
      line2
      postalCode
      state
    }
    preferredLocale
    taxId
    taxType
  }
  ... on PaymentCheck {
    __typename
  }
}
    ` as unknown as DocumentNode<PaymentMethodInfoFragment, unknown>;
export const SubscriptionBalanceInfo = /*#__PURE__*/ gql`
    fragment SubscriptionBalanceInfo on AccountSubscription {
  id
  balance
}
    ` as unknown as DocumentNode<SubscriptionBalanceInfoFragment, unknown>;
export const SubscriptionInfo = /*#__PURE__*/ gql`
    fragment SubscriptionInfo on AccountSubscription {
  ...SubscriptionBalanceInfo
  cancelledAt
  isLegacy
  cost
  discountOfferExpiresAt
  id
  nextBillAt
  addOns {
    cost
    id
    increment
    modifier
    quantity
  }
  limitedResources {
    archivedStorageBytes {
      limit53
      used53
    }
    mountedStorageBytes {
      limit53
      used53
    }
    projects {
      limit53
      used53
    }
    mountedStorageBytes {
      limit53
      used53
    }
    storageBytes {
      limit53
      used53
    }
    workspaces {
      limit53
      used53
    }
  }
  pauseOfferExpiresAt
  perUserStorage {
    seats
    increment
    limit
  }
  plan {
    activeStorageLimit
    archivedStorageLimit
    availableFeatures {
      id
      mountedStorage
      mountedStorageOverprovisioningAllowed
    }
    addOns {
      id
      modifier
    }
    cost
    id
    tier
    title
    eligibility
    mountedStorageLimit
    name
    userLimit
    userMax
    period
    perUserStorageIncrement
    version
  }
  period
  subscriptionEndAt
  tier
  title
  trial {
    isActive
    promotionExpiresAt
  }
}
    ${SubscriptionBalanceInfo}` as unknown as DocumentNode<SubscriptionInfoFragment, unknown>;
export const PlanSuggestionInfo = /*#__PURE__*/ gql`
    fragment PlanSuggestionInfo on PlanSuggestion {
  feature
  includedInCurrentPlan
  plan {
    cost
    id
    name
    period
    tier
    title
    userLimit
    version
  }
}
    ` as unknown as DocumentNode<PlanSuggestionInfoFragment, unknown>;
export const AccountOwner = /*#__PURE__*/ gql`
    fragment AccountOwner on Account {
  id
  owner {
    id
    name
    email
  }
}
    ` as unknown as DocumentNode<AccountOwnerFragment, unknown>;
export const AccountPlanAndTrialInfo = /*#__PURE__*/ gql`
    fragment AccountPlanAndTrialInfo on Account {
  id
  subscription {
    discountOfferExpiresAt
    id
    plan {
      cost
      id
      name
      paymentMethod
      period
      tier
      version
    }
    trial {
      isActive
    }
  }
}
    ` as unknown as DocumentNode<AccountPlanAndTrialInfoFragment, unknown>;
export const AccountManagedByAdobe = /*#__PURE__*/ gql`
    fragment AccountManagedByAdobe on Account {
  managedBy
}
    ` as unknown as DocumentNode<AccountManagedByAdobeFragment, unknown>;
export const AccountLockingInfo = /*#__PURE__*/ gql`
    fragment AccountLockingInfo on Account {
  id
  lockedAt
  lockReason
}
    ` as unknown as DocumentNode<AccountLockingInfoFragment, unknown>;
export const AccountRoleInfo = /*#__PURE__*/ gql`
    fragment AccountRoleInfo on Account {
  role {
    id
    name
  }
}
    ` as unknown as DocumentNode<AccountRoleInfoFragment, unknown>;
export const AccountLevelMetadataEnabled = /*#__PURE__*/ gql`
    fragment AccountLevelMetadataEnabled on Account {
  useAccountScopedFieldDefinitions
}
    ` as unknown as DocumentNode<AccountLevelMetadataEnabledFragment, unknown>;
export const AccountAdobeManagedDetails = /*#__PURE__*/ gql`
    fragment AccountAdobeManagedDetails on Account {
  managedBy
  adobeOrg
}
    ` as unknown as DocumentNode<AccountAdobeManagedDetailsFragment, unknown>;
export const AccountTierFields = /*#__PURE__*/ gql`
    fragment AccountTierFields on Account {
  id
  tier
}
    ` as unknown as DocumentNode<AccountTierFieldsFragment, unknown>;
export const AccountTrialStatus = /*#__PURE__*/ gql`
    fragment AccountTrialStatus on Account {
  id
  subscription {
    id
    trial {
      isActive
    }
  }
}
    ` as unknown as DocumentNode<AccountTrialStatusFragment, unknown>;
export const AccountPromotionalBannerOptOut = /*#__PURE__*/ gql`
    fragment AccountPromotionalBannerOptOut on Account {
  promotionalBannerOptOut
}
    ` as unknown as DocumentNode<AccountPromotionalBannerOptOutFragment, unknown>;

        

        
export const usePaymentMethodInfoFragment = (
  input: string | Omit<UseFragmentOptions<PaymentMethodInfoFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'PaymentMethod' } };
    return input;
  })();

  return useFragment<PaymentMethodInfoFragment>({
    fragment: PaymentMethodInfo,
    fragmentName: 'PaymentMethodInfo',
    ...options,
  });
};
  
        
        

        
export const useSubscriptionBalanceInfoFragment = (
  input: string | Omit<UseFragmentOptions<SubscriptionBalanceInfoFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'AccountSubscription' } };
    return input;
  })();

  return useFragment<SubscriptionBalanceInfoFragment>({
    fragment: SubscriptionBalanceInfo,
    fragmentName: 'SubscriptionBalanceInfo',
    ...options,
  });
};
  
        
        

        
export const useSubscriptionInfoFragment = (
  input: string | Omit<UseFragmentOptions<SubscriptionInfoFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'AccountSubscription' } };
    return input;
  })();

  return useFragment<SubscriptionInfoFragment>({
    fragment: SubscriptionInfo,
    fragmentName: 'SubscriptionInfo',
    ...options,
  });
};
  
        
        

        
export const usePlanSuggestionInfoFragment = (
  input: string | Omit<UseFragmentOptions<PlanSuggestionInfoFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'PlanSuggestion' } };
    return input;
  })();

  return useFragment<PlanSuggestionInfoFragment>({
    fragment: PlanSuggestionInfo,
    fragmentName: 'PlanSuggestionInfo',
    ...options,
  });
};
  
        
        

        
export const useAccountOwnerFragment = (
  input: string | Omit<UseFragmentOptions<AccountOwnerFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccountOwnerFragment>({
    fragment: AccountOwner,
    fragmentName: 'AccountOwner',
    ...options,
  });
};
  
        
        

        
export const useAccountPlanAndTrialInfoFragment = (
  input: string | Omit<UseFragmentOptions<AccountPlanAndTrialInfoFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccountPlanAndTrialInfoFragment>({
    fragment: AccountPlanAndTrialInfo,
    fragmentName: 'AccountPlanAndTrialInfo',
    ...options,
  });
};
  
        
        

        
export const useAccountManagedByAdobeFragment = (
  input: string | Omit<UseFragmentOptions<AccountManagedByAdobeFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccountManagedByAdobeFragment>({
    fragment: AccountManagedByAdobe,
    fragmentName: 'AccountManagedByAdobe',
    ...options,
  });
};
  
        
        

        
export const useAccountLockingInfoFragment = (
  input: string | Omit<UseFragmentOptions<AccountLockingInfoFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccountLockingInfoFragment>({
    fragment: AccountLockingInfo,
    fragmentName: 'AccountLockingInfo',
    ...options,
  });
};
  
        
        

        
export const useAccountRoleInfoFragment = (
  input: string | Omit<UseFragmentOptions<AccountRoleInfoFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccountRoleInfoFragment>({
    fragment: AccountRoleInfo,
    fragmentName: 'AccountRoleInfo',
    ...options,
  });
};
  
        
        

        
export const useAccountLevelMetadataEnabledFragment = (
  input: string | Omit<UseFragmentOptions<AccountLevelMetadataEnabledFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccountLevelMetadataEnabledFragment>({
    fragment: AccountLevelMetadataEnabled,
    fragmentName: 'AccountLevelMetadataEnabled',
    ...options,
  });
};
  
        
        

        
export const useAccountAdobeManagedDetailsFragment = (
  input: string | Omit<UseFragmentOptions<AccountAdobeManagedDetailsFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccountAdobeManagedDetailsFragment>({
    fragment: AccountAdobeManagedDetails,
    fragmentName: 'AccountAdobeManagedDetails',
    ...options,
  });
};
  
        
        

        
export const useAccountTierFieldsFragment = (
  input: string | Omit<UseFragmentOptions<AccountTierFieldsFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccountTierFieldsFragment>({
    fragment: AccountTierFields,
    fragmentName: 'AccountTierFields',
    ...options,
  });
};
  
        
        

        
export const useAccountTrialStatusFragment = (
  input: string | Omit<UseFragmentOptions<AccountTrialStatusFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccountTrialStatusFragment>({
    fragment: AccountTrialStatus,
    fragmentName: 'AccountTrialStatus',
    ...options,
  });
};
  
        
        

        
export const useAccountPromotionalBannerOptOutFragment = (
  input: string | Omit<UseFragmentOptions<AccountPromotionalBannerOptOutFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccountPromotionalBannerOptOutFragment>({
    fragment: AccountPromotionalBannerOptOut,
    fragmentName: 'AccountPromotionalBannerOptOut',
    ...options,
  });
};
  
        